import type {
  CategoryId,
  ContractId,
  MaterialId,
  PersonId,
  ProcurementType,
  SupplierId,
} from '../../data/types';

export type SourcingEventType = 'RFP' | 'RFQ' | 'RFI';

/**
 * How suppliers answer, in plain words. The type stays on the event as data;
 * screens lead with what is being bought and why, and show this only as a
 * small note under the stage.
 */
export const EVENT_FORMAT: Record<
  SourcingEventType,
  { label: string; hint: string }
> = {
  RFP: {
    label: 'Proposal',
    hint: 'Suppliers send full proposals: price, terms and how they would deliver',
  },
  RFQ: {
    label: 'Price quote',
    hint: 'Suppliers send prices for a fixed specification',
  },
  RFI: {
    label: 'Questions only',
    hint: 'Suppliers answer questions. No award comes from this step',
  },
};

/** What started a sourcing project: a record elsewhere in the demo, or a note that reads after "Started from". */
export type EventOrigin =
  | { kind: 'request'; ids: string[] }
  | { kind: 'agreement'; ids: ContractId[] }
  | { kind: 'risk'; ids: string[] }
  | { kind: 'note'; text: string };

export type SourcingStatus =
  | 'draft'
  | 'published'
  | 'evaluation'
  | 'ready_to_award'
  | 'awarded';

export type SavingsBasis = '3 yrs' | '2 yrs' | '1 yr' | 'one-time';

/**
 * One row of the sourcing events table. Everything here is plain JSON (dates
 * are day offsets from today) so it can live in the demo store.
 */
export interface SourcingEvent {
  id: string;
  title: string;
  type: SourcingEventType;
  procurementType: ProcurementType;
  categoryId: CategoryId;
  materialId?: MaterialId;
  status: SourcingStatus;
  invited: number;
  responded: number;
  /** Label shown above the date, e.g. "Award by", "Awarded". */
  dateLabel: string;
  /** Days from today; negative for past dates. */
  dateOffsetDays: number;
  /** Annual baseline spend in USD, when there is one. */
  baselineAnnual: number | null;
  /** Projected (or realized, once awarded) savings in USD. */
  savings: number | null;
  savingsBasis: SavingsBasis | null;
  ownerId: PersonId;
  /** Short note under the status badge. */
  statusNote?: string;
  /** Supplier the event was awarded to, when it is in the supplier master. */
  awardedSupplierId?: SupplierId;
  /** Free-text award recipient for suppliers outside the supplier master. */
  awardedToName?: string;
  awardedContractId?: ContractId;
  /** Intake request this event came from, e.g. REQ-1039. */
  requestId?: string;
  /** Why now, in one plain sentence. */
  why?: string;
  /** What started it, shown as "Started from" with links. */
  origins?: EventOrigin[];
  /** Replaces the default next-step line while the project is a draft. */
  nextStep?: string;
  /** Replaces the default AI recommendation line while the project is a draft. */
  recommendation?: string;
  /** Marks a row created in this session so the table can flag it. */
  isNew?: boolean;
}

/** The four bidders on RFP-2026-014, in column order. */
export type BidderId =
  | 'sup-aurelia'
  | 'sup-northgate'
  | 'sup-hanover'
  | 'sup-brightpak';

export const BIDDER_IDS: readonly BidderId[] = [
  'sup-aurelia',
  'sup-northgate',
  'sup-hanover',
  'sup-brightpak',
];

export type Shares = Record<BidderId, number>;
